import type { ApiEnvelope, ApiErrorItem } from "./envelope.schemas";

export interface ApiErrorInit {
  statusCode: number;
  code: string;
  message: string;
  errors?: ApiErrorItem[];
  traceId?: string | null;
  cause?: unknown;
}

/** Thrown for every failed call: transport failures, `success: false` envelopes and shape mismatches. */
export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly statusCode: number;
  readonly code: string;
  readonly errors: ApiErrorItem[];
  readonly traceId: string | null;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.statusCode = init.statusCode;
    this.code = init.code;
    this.errors = init.errors ?? [];
    this.traceId = init.traceId ?? null;
  }

  static fromEnvelope(envelope: ApiEnvelope, httpStatus: number): ApiError {
    const statusCode = envelope.statusCode || httpStatus;
    const first = envelope.errors?.[0];
    return new ApiError({
      statusCode,
      code: first?.code ?? `http.${statusCode}`,
      message: envelope.message ?? first?.message ?? defaultMessage(statusCode),
      errors: envelope.errors ?? [],
      traceId: envelope.traceId ?? null,
    });
  }

  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  get isValidation(): boolean {
    return this.statusCode === 400 || this.statusCode === 422;
  }

  /** `{ field: message }` for the errors that name a field, ready for `form.setError`. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const error of this.errors) {
      if (error.field && !(error.field in result)) result[error.field] = error.message;
    }
    return result;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function defaultMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "The request was invalid.";
    case 401:
      return "You need to sign in.";
    case 403:
      return "You do not have permission to do that.";
    case 404:
      return "Not found.";
    case 409:
      return "That conflicts with existing data.";
    case 422:
      return "The request could not be processed.";
    case 503:
      return "The service is temporarily unavailable.";
    default:
      return `Request failed with status ${statusCode}.`;
  }
}
